import { useProjects } from "@features/projects";
import CheckIcon from "@mui/icons-material/CheckRounded";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoardOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import ListAltIcon from "@mui/icons-material/ListAltRounded";
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useOrgStore } from "@stores/orgStore";
import { Link as RouterLink, useRouterState } from "@tanstack/react-router";
import { type MouseEvent as ReactMouseEvent, useState } from "react";

interface ProjectSwitcherProps {
  /** Project id (name) currently shown in the URL. */
  projectId: string;
}

// Every project surface that has its own URL segment. Used both for
// project-scoped (`/projects/$pid/<surface>`) and board-scoped
// (`/projects/$pid/boards/$bid/<surface>`) routes — the switcher
// always lands on the project-scoped variant, which then resolves
// the active board via the shell context (URL → store → first
// board).
//
// Keep this in sync with the `projectShellRoute` / `boardShellRoute`
// children in `app/router/router.tsx`. Missing entries silently
// drop the user on Overview after a project switch — surfaces like
// `connectors` and `brief` were absent until 2026-05 and that's how
// the "switching projects didn't change the connectors context" bug
// crept in: the user was already on Project A's connectors, switched
// to Project B, and the URL went to `.../overview` (mapped here)
// even though they expected to stay on Connectors.
const SURFACE_SEGMENTS = [
  "overview",
  "brief",
  "requirements",
  "cables",
  "bom",
  "schematic",
  "pcb",
  "graph",
  "circuits",
  "connectors",
  "versions",
  "sync",
  "chat",
] as const;
type SurfaceSegment = (typeof SURFACE_SEGMENTS)[number];

function currentSurface(pathname: string): SurfaceSegment {
  // Extract the LAST path segment (ignoring query / hash). Works for
  // both project-scoped paths (`/projects/A/connectors` → `connectors`)
  // and board-scoped paths (`/projects/A/boards/X/connectors` →
  // `connectors`). A pure regex on `/projects/<id>/<seg>` only
  // captures the first segment after the pid, which is `boards` for
  // board-scoped routes — that fell through to `overview` and was
  // half of the project-switch context bug.
  const cleaned = pathname.split("?")[0]?.split("#")[0] ?? "";
  const segments = cleaned.split("/").filter(Boolean);
  const last = segments[segments.length - 1];
  return last && (SURFACE_SEGMENTS as readonly string[]).includes(last)
    ? (last as SurfaceSegment)
    : "overview";
}

/**
 * Workspace title at the top of the project-mode sidebar.
 *
 * Shows the active project as a clickable pill with an expand caret.
 * Selecting a different project navigates to the same surface in the
 * other project (e.g. switching while on /bom keeps you on /bom).
 * "All projects" routes back to the project list.
 */
export function ProjectSwitcher({ projectId }: ProjectSwitcherProps) {
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const scopeOrgId = activeOrgId ?? "personal";
  // Unfiltered list for name lookup — the active project may be in a
  // different scope than the current OrgSwitcher selection during page load.
  const { data: allProjects } = useProjects();
  const { data: projects } = useProjects(scopeOrgId);
  const activeProjectName = allProjects?.find((p) => p.id === projectId)?.name;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const open = Boolean(anchor);
  const handleOpen = (e: ReactMouseEvent<HTMLButtonElement>) =>
    setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);

  const surface = currentSurface(pathname);

  return (
    <Box>
      <ButtonBase
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch project"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "border.default",
          bgcolor: "surface.raised",
          width: "100%",
          textAlign: "left",
          "&:hover": { bgcolor: "surface.raisedHover" },
        }}
      >
        <DeveloperBoardIcon sx={{ fontSize: 16, color: "secondary.main" }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 600,
              color: "text.disabled",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              lineHeight: 1.1,
            }}
          >
            Project
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.25,
            }}
          >
            {activeProjectName ?? projectId}
          </Typography>
        </Box>
        <IconButton
          size="small"
          tabIndex={-1}
          sx={{ p: "2px", color: "text.secondary" }}
          aria-label="Open project switcher"
        >
          <ExpandMoreIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: { sx: { minWidth: 240, maxHeight: 360 } },
        }}
      >
        {projects && projects.length > 0
          ? projects.map((p) => (
              <MenuItem
                key={p.id ?? `${p.name}-${p.organisation_id ?? "p"}`}
                component={RouterLink}
                to={`/projects/${p.id}/${surface}`}
                onClick={handleClose}
                selected={p.id === projectId}
                sx={{ textDecoration: "none", color: "inherit" }}
              >
                <ListItemIcon>
                  <DeveloperBoardIcon
                    fontSize="small"
                    sx={{ color: "secondary.main" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={p.name}
                  primaryTypographyProps={{
                    sx: { fontSize: "0.8125rem", fontWeight: 500 },
                  }}
                />
                {p.id === projectId && (
                  <CheckIcon
                    sx={{ fontSize: 16, color: "primary.main", ml: 1 }}
                  />
                )}
              </MenuItem>
            ))
          : null}

        {projects && projects.length > 0 && <Divider sx={{ my: 0.5 }} />}

        <MenuItem
          component={RouterLink}
          to="/projects"
          onClick={handleClose}
          sx={{ textDecoration: "none", color: "inherit" }}
        >
          <ListItemIcon>
            <ListAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="All projects" />
        </MenuItem>
      </Menu>
    </Box>
  );
}
